import { useEffect, useRef, useState } from 'react';
import api from '../api';
import { logger } from '../utils/logger';

/**
 * Debounced near-duplicate lookup for a draft task title.
 *
 * Returns [] until the title is meaningful (≥5 chars) and matches exist.
 * Pass `includeInbox: true` to also scan in-flight inbox items
 * (used by QuickCapture / CommandBar where the new item is going to
 * the inbox, not directly to tasks).
 */
export function useSimilarTasks(title, businessId, enabled = true, options = {}) {
  const { includeInbox = false } = options;
  const [matches, setMatches] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!enabled || !title || title.trim().length < 5) {
      setMatches([]);
      return undefined;
    }
    const controller = new AbortController();
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get('/tasks/similar', {
          params: {
            title: title.trim(),
            businessId: businessId || undefined,
            includeInbox: includeInbox || undefined,
          },
          signal: controller.signal,
        });
        setMatches(res.data);
      } catch (err) {
        if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
          logger.error('Similar task lookup failed:', err);
        }
      }
    }, 500);
    return () => { clearTimeout(timer.current); controller.abort(); };
  }, [title, businessId, enabled, includeInbox]);

  return matches;
}
